// Dashboard — витрина 7shifts design language.
// Метрики live из GET /reports/dashboard (право `reports:dashboard`).
// При загрузке показываем «—» (не роняем экран); при 403 — сообщение вместо цифр.
import {
  AccentCard,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DarkCard,
} from '@/components/ui/card';
import { useBranches } from '@/features/clients/hooks/useClients';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { branchSelectionLabel } from '@/lib/branch-label';
import { formatRub } from '@/lib/format';
import { useTenant } from '@/lib/tenant-context';

import { useDashboardSummary } from './dashboard.hooks';

const DASH = '—';

export function DashboardPage(): JSX.Element {
  const { tenantSlug, tenantId, branchIds } = useTenant();
  const { user } = useAuth();

  const summary = useDashboardSummary();

  // Имя филиала вместо сырого UUID (как в AppShell). Пустой выбор = «Все
  // филиалы»; при нескольких показываем их количество (план 106) — метрики
  // дашборда филиалом не фильтруются, это только подпись.
  const branchesQuery = useBranches();
  const branchName = branchSelectionLabel(
    branchIds
      .map((id) => branchesQuery.data?.find((b) => b.id === id))
      .filter((b): b is NonNullable<typeof b> => Boolean(b)),
  );

  // Выручка за месяц + кол-во продаж.
  const revenueLabel = summary.isSuccess ? formatRub(summary.data.revenue.net_kopeck) : DASH;
  const saleCount = summary.isSuccess ? summary.data.revenue.sale_count : null;
  // «Товары» и «Всего с товарами» рядом с выручкой (план 154 Ф5, ответ
  // владельца 3): выручка — услуги. Пока товаров нет — строк нет, как в кассе.
  const goodsKopeck = summary.isSuccess ? summary.data.revenue.goods_kopeck : 0;
  const totalWithGoodsKopeck = summary.isSuccess ? summary.data.revenue.total_with_goods_kopeck : 0;

  // Сделок в B24.
  const dealsLabel = summary.isSuccess ? String(summary.data.deals) : DASH;

  // Активные абонементы.
  const subsLabel = summary.isSuccess ? String(summary.data.active_subscriptions) : DASH;

  const forbidden = summary.error instanceof ApiError && summary.error.status === 403;

  return (
    <div className="space-y-6 bg-canvas-white">
      {/* Hero — Medium 48px заголовок, tracking -0.045em. */}
      <header className="space-y-2 pt-4">
        <h1 className="text-heading-lg font-medium text-ink-black">
          Добро пожаловать в Dance School CRM
        </h1>
        <p className="text-body text-muted-foreground">
          Школа танцев — управление расписанием, абонементами и кассой.
        </p>
      </header>

      {/* Сводка закрыта правом reports:dashboard — говорим об этом прямо,
          а не показываем три прочерка без объяснения. */}
      {forbidden ? (
        <p className="rounded-base bg-soft-orange/15 px-4 py-3 text-sm text-ink-black">
          Финансовая сводка доступна руководству. Ваши разделы — в меню слева.
        </p>
      ) : null}

      {/* Live-метрики (7shifts cards): выручка / сделки / абонементы. */}
      <div className="grid gap-6 md:grid-cols-3">
        {/* Выручка за месяц — Dark card. */}
        <DarkCard>
          <div className="space-y-3">
            <div className="text-caption uppercase tracking-snug text-canvas-white/60">
              Выручка за месяц
            </div>
            <div className="text-heading font-medium text-canvas-white">{revenueLabel}</div>
            {goodsKopeck !== 0 ? (
              <div data-testid="dashboard-goods" className="space-y-0.5 text-sm text-canvas-white">
                <div>
                  <span className="text-canvas-white/60">Товары: </span>
                  <span className="font-medium tabular-nums">{formatRub(goodsKopeck)}</span>
                </div>
                <div>
                  <span className="text-canvas-white/60">Всего с товарами: </span>
                  <span className="font-medium tabular-nums">
                    {formatRub(totalWithGoodsKopeck)}
                  </span>
                </div>
              </div>
            ) : null}
            <p className="text-sm text-canvas-white/70">
              {saleCount != null
                ? `${saleCount} продаж за текущий месяц.`
                : 'Касса консолидирует все филиалы.'}
            </p>
          </div>
        </DarkCard>

        {/* Сделок в B24 — Accent card (Lavender Mist). */}
        <AccentCard>
          <div className="space-y-3">
            <div className="text-caption uppercase tracking-snug text-ink-black/60">
              Сделок в B24
            </div>
            <div className="text-heading font-medium text-ink-black">{dealsLabel}</div>
            <p className="text-sm text-ink-black/70">Воронка «Отдел реализации ШТ».</p>
          </div>
        </AccentCard>

        {/* Активные абонементы — Canvas White card. */}
        <Card>
          <CardHeader>
            <CardDescription>Активные абонементы</CardDescription>
            <CardTitle>{subsLabel}</CardTitle>
          </CardHeader>
          <CardContent className="text-caption text-muted-foreground">
            Действующие подписки клиентов.
          </CardContent>
        </Card>
      </div>

      {/* Контекст сессии: tenant / филиал / пользователь. */}
      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardDescription>Школа</CardDescription>
            <CardTitle>{user?.tenantName ?? tenantSlug ?? DASH}</CardTitle>
          </CardHeader>
          <CardContent className="text-caption text-muted-foreground">
            tenant_id: {tenantId ?? user?.tenantId ?? 'не определён'}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardDescription>Текущий филиал</CardDescription>
            <CardTitle>{branchName}</CardTitle>
          </CardHeader>
          <CardContent className="text-caption text-muted-foreground">
            Селектор в AppShell.
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardDescription>Пользователь</CardDescription>
            <CardTitle>{user?.fullName ?? 'Не авторизован'}</CardTitle>
          </CardHeader>
          <CardContent className="text-caption text-muted-foreground">
            role: {user?.role ?? DASH}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
